import React, { useState, useMemo } from 'react';
import { Button } from 'primereact/button';
import { RadioButton } from 'primereact/radiobutton';
import { InputTextarea } from 'primereact/inputtextarea';
import { confirmDialog } from 'primereact/confirmdialog';
import { CoursePollWithQuestionsResponse } from '../../../../../api/types/students/user-details-student';
import {
  PollQuestionResponse,
  PollQuestionType,
} from '../../../../../api/types/settings/settings-all';
import {
  submitFeedbackAnswersOperation,
  FeedbackAnswer,
} from '../operations/submit-feedback-answers-operation';

export interface AnswerPollDialogProps {
  poll: CoursePollWithQuestionsResponse;
  programId: number;
  closeDialog: () => void;
}

export const AnswerPollDialog: React.FC<AnswerPollDialogProps> = ({
  poll,
  programId,
  closeDialog,
}) => {
  const [answers, setAnswers] = useState<Map<number, FeedbackAnswer>>(
    new Map(),
  );
  const [loading, setLoading] = useState(false);

  const sortedQuestions = useMemo(() => {
    return [...(poll.questions || [])].sort(
      (a, b) => a.questionOrder - b.questionOrder,
    );
  }, [poll.questions]);

  const handleOptionSelect = (questionId: number, optionId: number) => {
    setAnswers((prev) => {
      const newAnswers = new Map(prev);
      newAnswers.set(questionId, {
        questionId,
        selectedOptionId: optionId,
        textResponse: null,
      });
      return newAnswers;
    });
  };

  const handleTextChange = (questionId: number, text: string) => {
    setAnswers((prev) => {
      const newAnswers = new Map(prev);
      newAnswers.set(questionId, {
        questionId,
        selectedOptionId: null,
        textResponse: text,
      });
      return newAnswers;
    });
  };

  const getAnswer = (questionId: number): FeedbackAnswer | undefined => {
    return answers.get(questionId);
  };

  const handleSubmit = () => {
    confirmDialog({
      message: (
        <div style={{ textAlign: 'center' }}>
          <i
            className="pi pi-send"
            style={{
              fontSize: '2.5rem',
              color: '#3b82f6',
              marginBottom: '12px',
            }}
          />
          <p style={{ margin: '0 0 8px 0', fontWeight: 500 }}>
            האם אתה בטוח שברצונך לשלוח את המשוב?
          </p>
          <p style={{ margin: 0, color: '#6b7280', fontSize: '0.9rem' }}>
            לאחר השליחה לא ניתן יהיה לשנות את התשובות.
          </p>
        </div>
      ),
      header: 'שליחת משוב',
      icon: 'pi pi-question-circle',
      acceptLabel: 'שלח משוב',
      rejectLabel: 'ביטול',
      acceptClassName: 'p-button-primary',
      accept: async () => {
        const answersArray = Array.from(answers.values());
        await submitFeedbackAnswersOperation(
          programId,
          poll.courseId,
          poll.programCoursePollId,
          answersArray,
          setLoading,
          closeDialog,
        );
      },
    });
  };

  const renderQuestion = (question: PollQuestionResponse, index: number) => {
    const answer = getAnswer(question.id);
    const sortedOptions = [...(question.options || [])].sort(
      (a, b) => a.optionOrder - b.optionOrder,
    );

    return (
      <div
        key={question.id}
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          padding: '20px',
          marginBottom: '16px',
        }}
      >
        {/* Question Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px',
            marginBottom: '16px',
          }}
        >
          <span
            style={{
              width: '28px',
              height: '28px',
              borderRadius: '50%',
              backgroundColor: '#3b82f6',
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '14px',
              fontWeight: 'bold',
              flexShrink: 0,
            }}
          >
            {index + 1}
          </span>
          <div style={{ flex: 1 }}>
            <div
              style={{
                fontSize: '16px',
                fontWeight: 500,
                color: '#1f2937',
              }}
            >
              {question.questionText}
            </div>
            <div
              style={{
                fontSize: '12px',
                color: '#9ca3af',
                marginTop: '4px',
              }}
            >
              {question.questionType === PollQuestionType.multiple_choice
                ? 'בחר תשובה אחת'
                : 'כתוב את תשובתך'}
            </div>
          </div>
        </div>

        {/* Answer Options */}
        {question.questionType === PollQuestionType.multiple_choice ? (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              paddingRight: '40px',
            }}
          >
            {sortedOptions.map((option) => (
              <div
                key={option.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '12px 16px',
                  backgroundColor:
                    answer?.selectedOptionId === option.id
                      ? '#eff6ff'
                      : '#f9fafb',
                  borderRadius: '8px',
                  border:
                    answer?.selectedOptionId === option.id
                      ? '2px solid #3b82f6'
                      : '1px solid #e5e7eb',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                onClick={() => handleOptionSelect(question.id, option.id)}
              >
                <RadioButton
                  inputId={`option-${option.id}`}
                  checked={answer?.selectedOptionId === option.id}
                  onChange={() => handleOptionSelect(question.id, option.id)}
                />
                <label
                  htmlFor={`option-${option.id}`}
                  style={{
                    cursor: 'pointer',
                    color: '#374151',
                    flex: 1,
                  }}
                >
                  {option.optionText}
                </label>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ paddingRight: '40px' }}>
            <InputTextarea
              value={answer?.textResponse || ''}
              onChange={(e) => handleTextChange(question.id, e.target.value)}
              rows={4}
              style={{ width: '100%' }}
              placeholder="כתוב את תשובתך כאן..."
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ direction: 'rtl' }}>
      {/* Anonymous Notice */}
      {poll.isAnonymous && (
        <div
          style={{
            backgroundColor: '#f0f9ff',
            border: '1px solid #bae6fd',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
          }}
        >
          <i
            className="pi pi-shield"
            style={{ color: '#0284c7', fontSize: '18px' }}
          />
          <span style={{ color: '#0369a1', fontSize: '14px' }}>
            משוב זה הינו אנונימי - זהותך לא תוצג למרצה
          </span>
        </div>
      )}

      {/* Questions */}
      <div style={{ marginBottom: '20px' }}>
        {sortedQuestions.map((question, index) =>
          renderQuestion(question, index),
        )}
      </div>

      {/* Submit Button */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingTop: '16px',
          borderTop: '1px solid #e5e7eb',
        }}
      >
        <div style={{ fontSize: '13px', color: '#6b7280' }}>
          {answers.size} מתוך {sortedQuestions.length} שאלות נענו
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            label="ביטול"
            icon="pi pi-times"
            className="p-button-secondary p-button-outlined"
            onClick={closeDialog}
            disabled={loading}
          />
          <Button
            label="שלח משוב"
            icon="pi pi-send"
            onClick={handleSubmit}
            loading={loading}
          />
        </div>
      </div>
    </div>
  );
};
